// Odds conversion and formatting utilities

// Convert decimal odds to American odds
export const decimalToAmerican = (decimal) => {
  const d = parseFloat(decimal);
  if (isNaN(d) || d <= 1) return '+100';
  if (d >= 2.0) {
    return '+' + Math.round((d - 1) * 100);
  } else {
    return Math.round(-100 / (d - 1)).toString();
  }
};

// Convert American odds to decimal odds
export const americanToDecimal = (american) => {
  const a = parseFloat(american);
  if (isNaN(a)) return 2.0;
  if (a >= 100) {
    return (a / 100) + 1;
  } else if (a <= -100) {
    return (100 / Math.abs(a)) + 1;
  }
  return 2.0;
};

// Format odds based on selected format
export const formatOdds = (decimalOdds, format) => {
  if (format === 'American') {
    return decimalToAmerican(decimalOdds);
  }
  return parseFloat(decimalOdds).toFixed(2);
};

// Format market type for display
export const formatMarketType = (keytype, playerName) => {
  const typeMap = {
    'h2h': 'Moneyline',
    'spreads': 'Spread',
    'totals': 'Total',
    'player_points': 'Points',
    'player_points_alternate': 'Points (Alt)',
    'player_rebounds': 'Rebounds',
    'player_rebounds_alternate': 'Rebounds (Alt)',
    'player_assists': 'Assists',
    'player_assists_alternate': 'Assists (Alt)',
    'player_threes': '3-Pointers',
    'player_threes_alternate': '3-Pointers (Alt)',
    'player_points_rebounds_assists': 'PRA',
    'player_points_rebounds': 'Pts+Reb',
    'player_points_assists': 'Pts+Ast',
    'player_rebounds_assists': 'Reb+Ast',
    'player_shots_on_goal': 'Shots on Goal',
    'player_blocked_shots': 'Blocked Shots',
    'pitcher_strikeouts': 'Strikeouts',
    'batter_home_runs': 'Home Runs',
    'batter_hits': 'Hits',
    'batter_total_bases': 'Total Bases',
    'batter_rbis': 'RBIs',
    'batter_runs_scored': 'Runs Scored',
  };
  const marketLabel = typeMap[keytype] || keytype;
  return marketLabel;
};

// Sportsbook icon mapping
export const sportsbookIcons = {
  'ballybet': 'ballybet.jpeg',
  'betanysports': 'betanysports.jpeg',
  'betmgm': 'betmgm.jpeg',
  'betonlineag': 'betonlineag.png',
  'betparx': 'betparx.png',
  'betrivers': 'betrivers.jpeg',
  'betus': 'betus.jpeg',
  'bovada': 'bovada.jpeg',
  'draftkings': 'draftkings.jpeg',
  'espnbet': 'espnbet.jpeg',
  'fanatics': 'fanatics.jpeg',
  'fanduel': 'fanduel.png',
  'fliff': 'fliff.jpeg',
  'hardrockbet': 'hardrockbet.png',
  'lowvig': 'lowvig.jpg',
  'mybookieag': 'mybookieag.jpeg',
  'rebet': 'rebet.png',
  'williamhill_us': 'caesars.jpg'
};

// Sportsbook display names
export const sportsbookNames = {
  'ballybet': 'Bally Bet',
  'betanysports': 'BetAnySports',
  'betmgm': 'BetMGM',
  'betonlineag': 'BetOnline.ag',
  'betparx': 'betPARX',
  'betrivers': 'BetRivers',
  'betus': 'BetUS',
  'bovada': 'Bovada',
  'draftkings': 'DraftKings',
  'espnbet': 'ESPN Bet',
  'fanatics': 'Fanatics',
  'fanduel': 'FanDuel',
  'fliff': 'Fliff',
  'hardrockbet': 'Hard Rock Bet',
  'lowvig': 'LowVig.ag',
  'mybookieag': 'MyBookie.ag',
  'rebet': 'ReBet',
  'williamhill_us': 'Caesars'
};

// Format game time to user's local timezone
// Backend times are in UTC - this ensures proper conversion
export const formatGameTime = (utcTimeString) => {
  if (!utcTimeString) return '';

  // Parse the date - handle various formats
  let gameTime;

  // If it's already a proper ISO string with Z or timezone offset, parse directly
  if (utcTimeString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(utcTimeString)) {
    gameTime = new Date(utcTimeString);
  } else {
    // Assume UTC if no timezone indicator - append Z
    gameTime = new Date(utcTimeString + 'Z');
  }

  // Check if date is valid
  if (isNaN(gameTime.getTime())) {
    return utcTimeString; // Return original if parsing fails
  }

  return gameTime.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// LocalStorage key for odds format preference
const ODDS_FORMAT_KEY = 'oddsFormat';

// Get odds format from localStorage (defaults to 'American')
export const getOddsFormat = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(ODDS_FORMAT_KEY) || 'American';
  }
  return 'American';
};

// Save odds format to localStorage
export const setOddsFormat = (format) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(ODDS_FORMAT_KEY, format);
  }
};
